import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { useWindowWidth } from 'common/hooks';
import OffRampsHeading from 'offRamp/components/OffRampsHeading';
import Layout from 'common/containers/Layout';
import LoadingPage from 'common/components/LoadingPage';
import NewOffRampButton from 'offRamp/components/NewOffRampButton';
import { offRamp } from 'offRamp/propTypes';
import { selectors as sessionSelectors } from 'session';
import {
  kybBaseInfoSubmitted,
  kybStarted,
  kybT1InProgress,
  kybT1Submitted,
} from 'common/constants';
import { selectors as featuresSelectors } from 'features';

import NoOffRampsYet from '../components/NoOffRampsYet';
import OffRampsTable from './OffRampsTableDesktop';
import OffRampsAlerts from '../components/OffRampsAlerts';

import { getOffRampsTable, getOffRampsTableFilters } from '../reducers';
import actions from '../actions';
import { defaultPageSize, pageSizeOptions } from '../tableUtils';

import styles from './OffRamps.module.css';

const OffRamps = ({
  setPageSize,
  data,
  isFetching,
  totalElements,
  dateRangeValue,
  searchValue,
  statusValue,
  page,
  pageSize,
  isSandboxMode,
}) => {
  const tableRef = useRef();
  const noOffRamps = isEmpty(data);
  const windowWidth = useWindowWidth();

  const noDataYet =
    dateRangeValue === 'all' &&
    !searchValue &&
    statusValue === '.*' &&
    noOffRamps;

  const history = useHistory();
  const navigateToNewOffRamp = () => {
    history.push('/off-ramps/new');
  };

  const activeOrganization = useSelector(sessionSelectors.activeOrganization);
  const complianceStatus = activeOrganization?.complianceStatus;
  const isSSOActive = useSelector(featuresSelectors.isSSOActive);

  const kyb = useMemo(() => {
    const status = complianceStatus;
    const missing = [kybStarted, kybBaseInfoSubmitted].includes(status);
    const verifying = [kybT1InProgress, kybT1Submitted].includes(status);
    const showKybAlert = verifying && !isSSOActive;
    return { status, missing, verifying, showKybAlert };
  }, [complianceStatus, isSSOActive]);

  const navigateToVerifyBusiness = () => {
    history.push('/settlements', { showKyb: true });
  };

  const { showKybAlert } = kyb;

  const onOffRampIdClick = (offRampId) => {
    history.push(`/off-ramps/${offRampId}`);
  };

  // setPageSize action triggers offRampsRequest through sagas
  useEffect(() => {
    setPageSize(defaultPageSize);
  }, [setPageSize]);

  if (isFetching) {
    return (
      <Layout heading={<OffRampsHeading />}>
        <LoadingPage />
      </Layout>
    );
  }

  const renderMain = () => {
    if (noDataYet) {
      return (
        <>
          {showKybAlert && <OffRampsAlerts complianceStatus={kyb.status} />}
          <NoOffRampsYet
            kybNotSubmitted={kyb.missing}
            kybVerifying={kyb.verifying}
            onCreateOffRamp={navigateToNewOffRamp}
            onVerifyBusiness={navigateToVerifyBusiness}
            isSandboxMode={isSandboxMode}
          />
        </>
      );
    }

    return (
      <>
        {showKybAlert && <OffRampsAlerts complianceStatus={kyb.status} />}
        <div className={styles.newOffRampDesktopWrapper}>
          {!showKybAlert && (
            <NewOffRampButton navigateToNewOffRamp={navigateToNewOffRamp} />
          )}
        </div>
        <OffRampsTable
          data={data}
          isEmpty={!isFetching && data.length === 0}
          noDataYet={noDataYet}
          onOffRampIdClick={onOffRampIdClick}
          paginationHelperData={{
            page,
            pageSize,
            pageSizeOptions,
            setPageSize,
            totalElements,
          }}
          tableRef={tableRef}
          windowWidth={windowWidth}
        />
      </>
    );
  };

  return (
    <Layout
      heading={<OffRampsHeading />}
      sideContent={
        !noOffRamps &&
        !showKybAlert && (
          <div className={styles.newOffRampMobileWrapper}>
            <NewOffRampButton navigateToNewOffRamp={navigateToNewOffRamp} />
          </div>
        )
      }
    >
      <div className={styles.root}>
        <div className={styles.container}>{renderMain()}</div>
      </div>
    </Layout>
  );
};

OffRamps.propTypes = {
  setPageSize: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(offRamp).isRequired,
  isFetching: PropTypes.bool.isRequired,
  totalElements: PropTypes.number,
  dateRangeValue: PropTypes.string.isRequired,
  searchValue: PropTypes.string,
  statusValue: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  isSandboxMode: PropTypes.bool.isRequired,
};

const ConnectedOffRampsContainer = connect(
  (state) => {
    const { data, isFetching, totalElements, page, pageSize } =
      getOffRampsTable(state);
    const { dateRangeValue, searchValue, statusValue } =
      getOffRampsTableFilters(state);

    return {
      data,
      dateRangeValue,
      isFetching,
      page,
      pageSize,
      searchValue,
      statusValue,
      totalElements,
      isSandboxMode: featuresSelectors.isSandboxMode(state),
    };
  },
  {
    setPageSize: actions.setOffRampsTablePageSize,
  }
)(OffRamps);

export default ConnectedOffRampsContainer;
